import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { ErrorBoundary } from 'react-error-boundary'

import { useToggleStaticElements } from '../hooks/useToggleStaticElement'

interface PortalContextProps {
  /**
   * The root element this component will get rendered into
   */
  rootElement?: HTMLElement

  /**
   * Static (server rendered) element associated with this component.
   * Hide this element when the component gets rendered
   */
  staticElement?: HTMLElement

  toggleStaticElement: (isEnabled?: boolean) => void
}

export const PortalContext = React.createContext<PortalContextProps>({
  rootElement: undefined,
  staticElement: undefined,
  toggleStaticElement: () => {},
})

/**
 * Portal component props
 */
export interface PortalProps {
  /**
   * Server rendered element to replace with react component
   */
  staticElement?: Element | null

  /**
   * Immediately replace the static element with react children.
   * Turn this off if the child component is suspended
   *
   * @default true (if root exists)
   */
  toggleStaticElement?: boolean

  /**
   * Element to render the react components into
   */
  rootElement?: Element | null

  /**
   * Intentionally restricted to JSX element so that we don’t have to check children is valid react element
   */
  children: React.JSX.Element
}

export function Portal({ toggleStaticElement, staticElement, rootElement, children }: PortalProps) {
  /**
   * Set up element switching. We might want to hold off if we’re passing a suspendable component.
   * We can’t catch the promise here, it might have already been caught down the tree
   * and we want to avoid hiding the server-rendered element before the react content is ready
   */
  const [isEnabled, setIsEnabled] = React.useState<boolean>(toggleStaticElement ?? (!!staticElement && !!rootElement))
  useToggleStaticElements(staticElement, rootElement, isEnabled)

  const portalContextValue = React.useMemo(
    () => ({
      // Cast here to make it easier to pass any `Element`s as props
      rootElement: rootElement as HTMLElement,
      staticElement: staticElement as HTMLElement,
      // A way to get notified when children get rendered, because they could be suspended and waiting for requests
      toggleStaticElement: (enabled?: boolean) => setIsEnabled((prevValue) => enabled ?? !prevValue),
    }),
    [rootElement, staticElement]
  )

  if (!rootElement) return null

  const isChildFragment = React.isValidElement(children) && children.type === React.Fragment

  return (
    <ErrorBoundary fallback={null} onError={() => setIsEnabled(false)}>
      <PortalContext.Provider value={portalContextValue}>
        {ReactDOM.createPortal(
          React.cloneElement(children, isChildFragment ? {} : { staticElement, rootElement }),
          rootElement
        )}
      </PortalContext.Provider>
    </ErrorBoundary>
  )
}
